import { useEffect } from "react";
import type { PdfTemplateAiStatus } from "@workspace/api-client-react";

/** Recheck a server-provided transition; never submit a generation or mutate a draft. */
export function useArtworkAllowanceRefresh(ai: PdfTemplateAiStatus | undefined, refetch: () => unknown) {
  useEffect(() => {
    if (!ai?.retryAt) return;
    const at = Date.parse(ai.retryAt);
    if (!Number.isFinite(at)) return;
    // Background tabs and clock differences must not create a zero-delay loop.
    const timer = window.setTimeout(() => { void refetch(); }, Math.max(1000, at - Date.now() + 100));
    return () => window.clearTimeout(timer);
  }, [ai?.retryAt, refetch]);
}

export function ArtworkAllowance({ ai }: { ai: PdfTemplateAiStatus }) {
  const reason = ai.blockedReason ?? (!ai.unlimited && (ai.remaining ?? 0) <= 0 ? "personal_limit" : null);
  const message = reason === "generation_in_progress" ? "Artwork is already being generated for your account. Waiting for it to finish."
    : reason === "service_limit" ? "The artwork service has reached its rolling 24-hour limit. Availability will be checked automatically."
    : reason === "service_busy" ? "The artwork service is busy. Availability will be checked automatically."
    : null;
  return (
    <div id="artwork-allowance-help" className="space-y-1 text-[11px] leading-relaxed text-muted-foreground">
      <p>{ai.unlimited ? "Unlimited artwork attempts for your admin account. Each creates two images." : <>Up to {ai.limit} attempts in any 24 hours. Each creates two images. Once started, failed or discarded attempts count.</>}</p>
      {message && <p role="status" className="font-medium text-foreground" data-testid="text-artwork-blocked">{message}</p>}
    </div>
  );
}
